import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, UserCog, Mail, Phone, Pencil, ShieldCheck, Lock, BellRing, Users, ChevronDown, RefreshCw } from "lucide-react";

const PAY_TYPES = [
  { k: "owner", label: "Owner (record only)" },
  { k: "hourly", label: "Hourly (on timesheets)" },
  { k: "salary", label: "Salary (record only)" },
  { k: "contract", label: "Contract (record only)" },
];
const payLabel = (k) => (PAY_TYPES.find((p) => p.k === k) || PAY_TYPES[1]).label;

export default function Managers() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = !!user?.can_manage_managers;
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", pay_type: "salary", can_manage_managers: false, stuck_alerts: false,
  });

  const { data: managers = [], isLoading } = useQuery({
    queryKey: ["managers"],
    queryFn: async () => (await api.get("/managers")).data,
  });

  const create = useMutation({
    mutationFn: () => api.post("/managers", form),
    onSuccess: () => {
      toast.success(t("Manager added"));
      setOpen(false);
      setForm({ name: "", email: "", password: "", phone: "", pay_type: "salary", can_manage_managers: false, stuck_alerts: false });
      qc.invalidateQueries({ queryKey: ["managers"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/managers/${id}`, body),
    onSuccess: () => {
      toast.success(t("Manager updated"));
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["managers"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/managers/${id}`),
    onSuccess: () => {
      toast.success(t("Manager removed"));
      setToDelete(null);
      qc.invalidateQueries({ queryKey: ["managers"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{t("Owners / Managers")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("People who run the motel. Set pay type and who's allowed to manage other managers.")}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setOpen(true)} data-testid="add-manager-btn" className="k-press gap-2">
            <Plus className="h-4 w-4" /> {t("Add Manager")}
          </Button>
        )}
      </div>

      {!canManage && (
        <div
          className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-4 py-2.5 text-sm text-muted-foreground"
          data-testid="no-privilege-note"
        >
          <Lock className="h-4 w-4" /> {t("You can view the team, but only managers with the \"Can manage managers\" permission can add or edit managers.")}
        </div>
      )}

      <TeamPresencePanel />

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid gap-3" data-testid="managers-grid">
          {managers.map((m) => {
            const disabled = m.active === false;
            return (
              <Card
                key={m.id}
                data-testid={`manager-card-${m.id}`}
                className={`rounded-md border p-4 ${disabled ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary font-display font-bold">
                      {m.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2 font-semibold">
                        {m.name}
                        {m.is_self && (
                          <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">
                            {t("You")}
                          </span>
                        )}
                        {m.can_manage_managers && (
                          <span
                            data-testid={`priv-badge-${m.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-700"
                          >
                            <ShieldCheck className="h-3 w-3" /> {t("Can manage")}
                          </span>
                        )}
                        {m.stuck_alerts && (
                          <span
                            data-testid={`stuck-alert-badge-${m.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700"
                          >
                            <BellRing className="h-3 w-3" /> {t("Stuck alerts")}
                          </span>
                        )}
                        {disabled && (
                          <span
                            data-testid={`manager-disabled-badge-${m.id}`}
                            className="rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase text-rose-700"
                          >
                            {t("Disabled")}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Mail className="h-3 w-3" /> {m.email}
                      </div>
                      {m.phone ? (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" /> {m.phone}
                        </div>
                      ) : null}
                      <div className="mt-1.5">
                        <span className="inline-flex items-center rounded-md border border-border bg-secondary/50 px-2 py-0.5 text-xs font-semibold">
                          {t(payLabel(m.pay_type))}
                        </span>
                      </div>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setEditing(m)}
                        data-testid={`edit-manager-${m.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {!m.is_self && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setToDelete(m)}
                          data-testid={`delete-manager-${m.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add manager */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-manager-dialog">
          <DialogHeader>
            <DialogTitle>{t("Add Manager")}</DialogTitle>
            <DialogDescription>{t("Create a login for an owner or manager.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("Full name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="manager-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("Phone (optional)")}</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="manager-phone-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("Email")}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="manager-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("Temporary password")}</Label>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="manager-password-input" placeholder={t("Set a password")} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("Pay type")}</Label>
              <select
                value={form.pay_type}
                onChange={(e) => setForm({ ...form, pay_type: e.target.value })}
                data-testid="manager-pay-type-select"
                className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {PAY_TYPES.map((p) => (
                  <option key={p.k} value={p.k}>{t(p.label)}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">{t("Hourly managers appear on timesheets/pay periods. Salary & contract are record-only.")}</p>
            </div>
            <label className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-semibold">{t("Can manage managers")}</div>
                <div className="text-xs text-muted-foreground">{t("Allow this person to add/edit/remove other managers.")}</div>
              </div>
              <Switch
                checked={form.can_manage_managers}
                onCheckedChange={(v) => setForm({ ...form, can_manage_managers: v })}
                data-testid="manager-privilege-switch"
              />
            </label>
            <label className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-semibold">{t("Receive stuck-task alerts")}</div>
                <div className="text-xs text-muted-foreground">{t("Get an in-app (and email) alert when a task is left running past shift end.")}</div>
              </div>
              <Switch
                checked={form.stuck_alerts}
                onCheckedChange={(v) => setForm({ ...form, stuck_alerts: v })}
                data-testid="manager-stuck-alerts-switch"
              />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!form.name || !form.email || !form.password || create.isPending}
              data-testid="save-manager-btn"
              className="gap-2"
            >
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("Add manager")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditManagerDialog
        manager={editing}
        pending={update.isPending}
        onClose={() => setEditing(null)}
        onSave={(body) => update.mutate({ id: editing.id, ...body })}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Remove {{name}}?", { name: toDelete?.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("This permanently deletes their login. To keep access records, use \"Disable\" instead.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => del.mutate(toDelete.id)}
              data-testid="confirm-delete-manager"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("Remove")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditManagerDialog({ manager, onClose, onSave, pending }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(true);
  const [payType, setPayType] = useState("salary");
  const [canManage, setCanManage] = useState(false);
  const [stuckAlerts, setStuckAlerts] = useState(false);

  useEffect(() => {
    if (manager) {
      setName(manager.name || "");
      setEmail(manager.email || "");
      setPhone(manager.phone || "");
      setPassword("");
      setActive(manager.active !== false);
      setPayType(manager.pay_type || "salary");
      setCanManage(!!manager.can_manage_managers);
      setStuckAlerts(!!manager.stuck_alerts);
    }
  }, [manager]);

  if (!manager) return null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-manager-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Edit {{name}}", { name: manager.name })}</DialogTitle>
          <DialogDescription>{t("Update details, pay type and permissions.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="text-sm font-semibold">{t("Account active")}</div>
              <div className="text-xs text-muted-foreground">{t("Disabled managers can't log in.")}</div>
            </div>
            <Switch
              checked={active}
              onCheckedChange={setActive}
              disabled={manager.is_self}
              data-testid="manager-active-switch"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("Name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-manager-name" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("Phone")}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="edit-manager-phone" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("Login email")}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-manager-email" />
          </div>

          <div className="space-y-1.5">
            <Label>{t("Reset password (optional)")}</Label>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("Leave blank to keep current")} data-testid="edit-manager-password" />
          </div>

          <div className="space-y-1.5">
            <Label>{t("Pay type")}</Label>
            <select
              value={payType}
              onChange={(e) => setPayType(e.target.value)}
              data-testid="edit-manager-pay-type"
              className="block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              {PAY_TYPES.map((p) => (
                <option key={p.k} value={p.k}>{t(p.label)}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="text-sm font-semibold">{t("Can manage managers")}</div>
              <div className="text-xs text-muted-foreground">
                {manager.is_self ? t("You can't remove your own permission.") : t("Allow adding/editing/removing managers.")}
              </div>
            </div>
            <Switch
              checked={canManage}
              onCheckedChange={setCanManage}
              disabled={manager.is_self}
              data-testid="edit-manager-privilege-switch"
            />
          </label>

          <label className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <div className="text-sm font-semibold">{t("Receive stuck-task alerts")}</div>
              <div className="text-xs text-muted-foreground">
                {t("Get an in-app (and email) alert when a task is left running past shift end.")}
              </div>
            </div>
            <Switch
              checked={stuckAlerts}
              onCheckedChange={setStuckAlerts}
              data-testid="edit-manager-stuck-alerts-switch"
            />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancel")}</Button>
          <Button
            disabled={pending || !name || !email}
            onClick={() =>
              onSave({
                name,
                email,
                phone,
                active,
                pay_type: payType,
                can_manage_managers: canManage,
                stuck_alerts: stuckAlerts,
                ...(password ? { password } : {}),
              })
            }
            data-testid="save-edit-manager-btn"
            className="gap-2"
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PRESENCE_META = {
  online: { dot: "bg-emerald-500", ring: "ring-emerald-500/30", label: "Online now" },
  recent: { dot: "bg-amber-400", ring: "ring-amber-400/30", label: "Seen today" },
  stale: { dot: "bg-slate-300", ring: "ring-slate-300/40", label: "Away 8h+" },
};

function lastSeenLabel(iso, nowIso, t) {
  if (!iso) return t("Not signed in");
  const secs = (new Date(nowIso).getTime() - new Date(iso).getTime()) / 1000;
  if (secs < 60) return t("just now");
  const mins = Math.floor(secs / 60);
  if (mins < 60) return t("{{n}}m ago", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("{{n}}h ago", { n: hrs });
  const days = Math.floor(hrs / 24);
  return t("{{n}}d ago", { n: days });
}

function PresenceRow({ person, nowIso }) {
  const { t } = useTranslation();
  const meta = PRESENCE_META[person.presence] || PRESENCE_META.stale;
  return (
    <div
      className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2"
      data-testid={`presence-row-${person.id}`}
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        {person.presence === "online" && (
          <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${meta.dot} opacity-60`} />
        )}
        <span
          className={`relative inline-flex h-2.5 w-2.5 rounded-full ${meta.dot} ring-2 ${meta.ring}`}
          data-testid={`presence-dot-${person.id}`}
          data-presence={person.presence}
        />
      </span>
      <div className="min-w-0 flex-1 truncate text-sm font-medium">{person.name}</div>
      <div className="shrink-0 text-xs text-muted-foreground" data-testid={`presence-lastseen-${person.id}`}>
        {lastSeenLabel(person.last_seen, nowIso, t)}
      </div>
    </div>
  );
}

function PresenceGroup({ title, online, total, people, nowIso, testid }) {
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">{title}</div>
        <span
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700"
          data-testid={`${testid}-count`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          {online} / {total} online
        </span>
      </div>
      <div className="grid gap-1.5">
        {people.length ? (
          people.map((p) => <PresenceRow key={p.id} person={p} nowIso={nowIso} />)
        ) : (
          <div className="rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">—</div>
        )}
      </div>
    </div>
  );
}

function TeamPresencePanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["team-presence"],
    queryFn: async () => (await api.get("/team-presence")).data,
    refetchInterval: 30000,
  });

  const mgrOnline = data?.managers_online ?? 0;
  const mgrTotal = data?.managers_total ?? 0;
  const clnOnline = data?.cleaners_online ?? 0;
  const clnTotal = data?.cleaners_total ?? 0;

  return (
    <Card className="overflow-hidden rounded-md border" data-testid="team-presence-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/40"
        data-testid="team-presence-toggle"
      >
        <div className="flex items-center gap-2.5">
          <Users className="h-4 w-4 text-indigo-500" />
          <div>
            <div className="text-sm font-semibold">{t("Who's online")}</div>
            <div className="text-xs text-muted-foreground">
              {t("Managers")}: <b className="text-foreground">{mgrOnline}/{mgrTotal}</b>
              {"  ·  "}
              {t("Cleaners")}: <b className="text-foreground">{clnOnline}/{clnTotal}</b>
            </div>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-border px-4 py-4">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {t("Online now")}</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400" /> {t("Seen today")}</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-slate-300" /> {t("Away 8h+")}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 px-2 text-xs"
                  onClick={() => refetch()}
                  data-testid="team-presence-refresh"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> {t("Refresh")}
                </Button>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                <PresenceGroup
                  title={t("Owners / Managers")}
                  online={mgrOnline}
                  total={mgrTotal}
                  people={data?.managers || []}
                  nowIso={data?.now}
                  testid="presence-managers"
                />
                <PresenceGroup
                  title={t("Cleaners")}
                  online={clnOnline}
                  total={clnTotal}
                  people={data?.cleaners || []}
                  nowIso={data?.now}
                  testid="presence-cleaners"
                />
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
